import {
  deleteArticuloById,
  deleteMultipleArticulos,
  getArticuloById,
  getArticulosByFilter,
  getArticulosParams,
} from "api/Articulo.service"
import { ArticuloReadingDtoProps } from "api/DTOs/ArticuloReadingDto"
import axios from "axios"
import { STATUSCORRECT } from "common/components/ModalVerDocumento/constants"
import { useDownloadFileContext } from "common/context/DownloadFileProvider"
import notification from "common/utils/notification"
import mapArticulosObjectToExcel from "features/Inventario/mappers/mapArticulosObjectToExcel"
import { useFilterMenu } from "hooks/useFilterMenu"
import { useCallback, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

const useArticulos = () => {
  const { t } = useTranslation()
  const [selectedMultipleArticulos, setSelectedMultipleArticulos] = useState<
    Array<ArticuloReadingDtoProps>
  >([])
  const [selectedArticulo, setSelectedArticulo] =
    useState<ArticuloReadingDtoProps | null>(null)
  const [selectedArticuloToDelete, setSelectedArticuloToDelete] =
    useState<ArticuloReadingDtoProps | null>(null)
  const [showArticuloModal, setShowArticuloModal] = useState({
    show: false,
    isEdit: false,
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false)

  const { handleDownloadExcel } = useDownloadFileContext()

  const articulosByFilterCancelToken = useRef(axios.CancelToken.source())
  const fetchItems = useCallback(() => {
    return (queryState: getArticulosParams) => {
      articulosByFilterCancelToken.current?.cancel()
      articulosByFilterCancelToken.current = axios.CancelToken.source()
      // categoriaItemsInvalidation()
      return getArticulosByFilter(
        {
          ...queryState,
          MaxResultCount: queryState.MaxResultCount ?? 100,
          SkipCount: queryState.SkipCount ?? 0,
        },
        articulosByFilterCancelToken.current.token
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { items, isLoading, totalCount, setServiceQuery, fetchTableData } =
    useFilterMenu({
      serviceCall: fetchItems,
    })

  const editArticulo = useCallback(async ({ id }: { id: string }) => {
    const articuloById = await getArticuloById(id)
    setSelectedArticulo(articuloById)
    setShowArticuloModal({ show: true, isEdit: true })
  }, [])

  const handleAskForDelete = useCallback(
    (articulo: ArticuloReadingDtoProps) => {
      setSelectedArticuloToDelete(articulo)
      setShowDeleteModal(true)
    },
    [setShowDeleteModal]
  )

  const deleteArticulo = async () => {
    if (!selectedArticuloToDelete?.id) return
    try {
      const { status }: { status: number } = await deleteArticuloById(
        selectedArticuloToDelete.id
      )
      if (status === STATUSCORRECT) {
        setShowDeleteModal(false)
        fetchTableData()
        notification.success({
          body: `${t("notification.articleCorrectDelete")}`,
        })
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
  }

  const deleteMultiple = async () => {
    try {
      const ids = selectedMultipleArticulos.map((articulo) => articulo.id)
      const { status }: { status: number } = await deleteMultipleArticulos(ids)
      if (status === STATUSCORRECT) {
        setShowDeleteModal(false)
        setSelectedMultipleArticulos([])
        fetchTableData()
        notification.success({
          body: `${t("notification.articleSCorrectDelete")}`,
        })
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
  }

  const downloadExcel = async () => {
    setIsDownloadingExcel(true)

    try {
      const res = await getArticulosByFilter({ IsExportEnabled: true })
      const articulos = mapArticulosObjectToExcel(res.items, t)
      handleDownloadExcel(articulos, "articulos")
      notification.success({
        body: `${t("notification.excelDownloadSuccess")}`,
      })
      setIsDownloadingExcel(false)
    } catch (e) {
      setIsDownloadingExcel(false)
    }
  }

  return {
    selectedArticulo,
    showDeleteModal,
    setShowDeleteModal,
    selectedMultipleArticulos,
    showArticuloModal,
    isDownloadingExcel,
    selectedArticuloToDelete,
    items,
    isLoading,
    totalCount,
    setServiceQuery,
    fetchTableData,
    setShowArticuloModal,
    editArticulo,
    deleteArticulo,
    handleAskForDelete,
    deleteMultiple,
    setSelectedMultipleArticulos,
    downloadExcel,
  }
}

export default useArticulos
